import { removeBlankPdfPages } from "../../utils/pdfPagination";
import React, { useEffect, useState, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import PatientDetailsComponent from "../patientDetailsComponent/patientDetailsComponent";
import config from "../../config";
import axios from "axios";
import LoadingComponent from "../loadingComponent/loadingComponent";
import InferenceDetailsComponent from "../inferenceDetailsComponent/inferenceDetailsComponent";
import DisclaimerComponent from "../disclaimerComponent/disclaimerComponent";
import { handleSetReferral, handleSetDR, nextSteps, getInferenceFromData } from "../../utlis";
import { useLocation, useNavigate } from "react-router-dom";
import ProcessingScreen from "../processingScreen/processingScreen";
import { formatDate } from "../../utils/patientUtils";
import PdfTemplate from "../pdfTemplate/pdfTemplate";
import html2pdf from "html2pdf.js";
import { isOffline } from "../../utils/offlineDetection";
import { initOpenCVWorker } from "../../services/opencvWorkerManager";


const PatientInference = ({
    formik,
    inference,
    handleExit,
    handlePatientDetailsEditClick,
    handleImageEditClick,
    handleHealthEditClick,
    handleSubmitReport,
    submittingReport,
    reportSubmitted,
    setStep,
    setInferenceState,
    setConsent,
    onAddNewPatient
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [visitNumber, setVisitNumber] = useState(null);
    const [loadingVisitNumber, setLoadingVisitNumber] = useState(true);
    const [downloadPdfFlag, setDownloadPdfFlag] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [isOnline, setIsOnline] = useState(true);
    const [leftImageSrc, setLeftImageSrc] = useState(null);
    const [rightImageSrc, setRightImageSrc] = useState(null);

    // Detect Safari browser
    const isSafari = useMemo(() => {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }, []);

    // Get image files from formik
    const leftImage = useMemo(() => formik.values.image_file?.left?.image, [formik.values.image_file?.left?.image]);
    const rightImage = useMemo(() => formik.values.image_file?.right?.image, [formik.values.image_file?.right?.image]);

    // Convert left image to URL with cleanup
    useEffect(() => {
        let blobUrl = null;

        const createImageSrc = async () => {
            if (!leftImage) {
                setLeftImageSrc(null);
                return;
            }

            // If it's already a URL (string), use it directly
            if (typeof leftImage === 'string') {
                setLeftImageSrc(leftImage);
                return;
            }

            // CRITICAL: Check for File/Blob FIRST - before any other checks!
            // File objects don't have enumerable keys, so Object.keys() returns []
            if (leftImage instanceof File || leftImage instanceof Blob) {
                blobUrl = URL.createObjectURL(leftImage);
                setLeftImageSrc(blobUrl);
                return;
            }

            // Handle ArrayBuffer format (from IndexedDB storage - new format)
            if (leftImage && typeof leftImage === 'object' && leftImage.data && leftImage.type) {
                try {
                    // Check if data is ArrayBuffer or needs conversion
                    let arrayBuffer = leftImage.data;
                    
                    // If data is already an ArrayBuffer, use it directly
                    if (arrayBuffer instanceof ArrayBuffer) {
                        const blob = new Blob([arrayBuffer], { type: leftImage.type });
                        blobUrl = URL.createObjectURL(blob);
                        setLeftImageSrc(blobUrl);
                        return;
                    }
                    
                    // If data is a Uint8Array or similar, convert to ArrayBuffer
                    if (arrayBuffer instanceof Uint8Array || Array.isArray(arrayBuffer)) {
                        const blob = new Blob([arrayBuffer], { type: leftImage.type });
                        blobUrl = URL.createObjectURL(blob);
                        setLeftImageSrc(blobUrl);
                        return;
                    }
                } catch (error) {
                    console.warn('Error creating blob from ArrayBuffer for left image:', error);
                }
            }

            // Fallback - set to null if we can't process the image
            setLeftImageSrc(null);
        };

        createImageSrc();

        // Cleanup: revoke blob URL when component unmounts or image changes
        return () => {
            if (blobUrl) {
                try {
                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    console.warn('Error revoking left image blob URL:', error);
                }
            }
        };
    }, [leftImage, isSafari]);

    // Convert right image to URL with cleanup
    useEffect(() => {
        let blobUrl = null;

        const createImageSrc = async () => {
            if (!rightImage) {
                setRightImageSrc(null);
                return;
            }

            // If it's already a URL (string), use it directly
            if (typeof rightImage === 'string') {
                setRightImageSrc(rightImage);
                return;
            }

            // CRITICAL: Check for File/Blob FIRST - before any other checks!
            // File objects don't have enumerable keys, so Object.keys() returns []
            if (rightImage instanceof File || rightImage instanceof Blob) {
                blobUrl = URL.createObjectURL(rightImage);
                setRightImageSrc(blobUrl);
                return;
            }

            // Handle ArrayBuffer format (from IndexedDB storage - new format)
            if (rightImage && typeof rightImage === 'object' && rightImage.data && rightImage.type) {
                try {
                    // Check if data is ArrayBuffer or needs conversion
                    let arrayBuffer = rightImage.data;
                    
                    // If data is already an ArrayBuffer, use it directly
                    if (arrayBuffer instanceof ArrayBuffer) {
                        const blob = new Blob([arrayBuffer], { type: rightImage.type });
                        blobUrl = URL.createObjectURL(blob);
                        setRightImageSrc(blobUrl);
                        return;
                    }
                    
                    // If data is a Uint8Array or similar, convert to ArrayBuffer
                    if (arrayBuffer instanceof Uint8Array || Array.isArray(arrayBuffer)) {
                        const blob = new Blob([arrayBuffer], { type: rightImage.type });
                        blobUrl = URL.createObjectURL(blob);
                        setRightImageSrc(blobUrl);
                        return;
                    }
                } catch (error) {
                    console.warn('Error creating blob from ArrayBuffer for right image:', error);
                }
            }

            // Fallback - set to null if we can't process the image
            setRightImageSrc(null);
        };

        createImageSrc();

        // Cleanup: revoke blob URL when component unmounts or image changes
        return () => {
            if (blobUrl) {
                try {
                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    console.warn('Error revoking right image blob URL:', error);
                }
            }
        };
    }, [rightImage, isSafari]);

    useEffect(() => {
        // Push current state to history so popstate can detect backward navigation
        window.history.pushState(null, "", location.pathname);
      
        const handlePopState = (event) => {
          // If report is already submitted, navigate to dashboard instead of going back to form
          if (reportSubmitted) {
            navigate("/dashboard");
            return;
          }
          
          // Only allow going back if report is not submitted
          setStep(1);
          setConsent(true);
          setInferenceState(prev => ({
            ...prev,
            inferenceProvided: false,
        }));
        };
      
        window.addEventListener("popstate", handlePopState);
      
        return () => {
          window.removeEventListener("popstate", handlePopState);
        };
      }, [reportSubmitted, navigate, setStep, setConsent, setInferenceState]);
    
    // When report is submitted, push a new history state to prevent back navigation to form
    useEffect(() => {
        if (reportSubmitted) {
            // Push a new state so back button doesn't go to form
            window.history.pushState(null, "", location.pathname);
        }
    }, [reportSubmitted, location.pathname]);

      useEffect(() => {
        const beforeUnloadHandler = (e) => {
          // Only show dialog if there's unsaved data (patient_id exists)
          const patientId = localStorage.getItem("patient_id");
          if (patientId) {
          e.preventDefault();
          e.returnValue = ''; // Some browsers require this to trigger the dialog
          }
        };
      
        window.addEventListener('beforeunload', beforeUnloadHandler);
      
        return () => {
          window.removeEventListener('beforeunload', beforeUnloadHandler);
        };
      }, []);

      useEffect(() => {
        // Check online status
        isOffline().then(offline => {
          setIsOnline(!offline);
        });
        
        const patientId = localStorage.getItem("patient_id");
        if (!patientId) {
          setLoadingVisitNumber(false);
          return;
        }
      
        const header = {
          Authorization: "Token " + localStorage.getItem("optha-token"),
        };
      
        axios
          .get(`${config.API_BASE_URL}/api/reports/getreport/?patient_id=${patientId}`, {
            headers: header,
          })
          .then((response) => {
            setVisitNumber(response.data.count);
          })
          .catch((error) => {
            console.log(error);
            // If API call fails and we're online, set visit number to 1 for new patient (first visit)
            isOffline().then(offline => {
              if (!offline) {
                setVisitNumber(1); // New patient, first visit
              }
            });
          })
          .finally(() => {
            setLoadingVisitNumber(false);
          });
      }, []);
      
      

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleHomeClick = () => {
        // Clear report_id to prevent PrintDialogBox from showing
        localStorage.removeItem("report_id");
        navigate("/dashboard");
    };

    const handleAddNewPatient = () => {
        // If parent provides handler (screening flow), use it to reset state and show form again
        if (onAddNewPatient) {
            onAddNewPatient();
            return;
        }
        // Fallback: clear storage and navigate (e.g. if used outside wrapper)
        localStorage.removeItem("patient_id");
        localStorage.removeItem("is_follow_up_patient");
        localStorage.removeItem("follow_up_other_id");
        localStorage.removeItem("report_id");
        initOpenCVWorker().catch((err) => {
            console.error('OpenCV worker init failed:', err);
        });
        navigate("/patientscreen", { replace: true });
    };

    // Convert images to data URLs - same pattern as ExportDialog
    function imagetoBase64(image1, image2) {
        const convertToDataURL = async (img) => {
            if (!img) return null;
            
            // If already a data URL, return as-is
            if (typeof img === 'string' && img.startsWith('data:image/')) {
                return img;
            }
            
            // If it's a File or Blob, convert directly
            if (img instanceof File || img instanceof Blob) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => reject(new Error('Failed to convert to data URL'));
                    reader.readAsDataURL(img);
                });
            }
            
            // Handle ArrayBuffer format (from IndexedDB storage - new format)
            if (img && typeof img === 'object' && img.data && img.type) {
                try {
                    let arrayBuffer = img.data;
                    
                    // If data is already an ArrayBuffer, use it directly
                    if (arrayBuffer instanceof ArrayBuffer) {
                        const blob = new Blob([arrayBuffer], { type: img.type });
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = () => reject(new Error('Failed to convert ArrayBuffer to data URL'));
                            reader.readAsDataURL(blob);
                        });
                    }
                    
                    // If data is a Uint8Array or similar, convert to ArrayBuffer
                    if (arrayBuffer instanceof Uint8Array || Array.isArray(arrayBuffer)) {
                        const blob = new Blob([arrayBuffer], { type: img.type });
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = () => reject(new Error('Failed to convert ArrayBuffer to data URL'));
                            reader.readAsDataURL(blob);
                        });
                    }
                } catch (error) {
                    console.warn('Error converting ArrayBuffer to data URL:', error);
                    return null;
                }
            }
            
            // If it's a blob URL, fetch and convert
            if (typeof img === 'string' && img.startsWith('blob:')) {
                return fetch(img)
                    .then(response => response.blob())
                    .then(blob => {
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = () => reject(new Error('Failed to convert to data URL'));
                            reader.readAsDataURL(blob);
                        });
                    });
            }
            
            return null;
        };
        
        return Promise.all([
            convertToDataURL(image1),
            convertToDataURL(image2)
        ])
        .then((dataURLs) => {
            return {
                left: dataURLs[0],
                right: dataURLs[1],
            };
        })
        .catch((error) => {
            return { left: null, right: null };
        });
    }

    const generatePDF = async () => {
        if (!pdfData) {
            return;
        }
        
        const element = document.getElementById("pdf-content");
        
        if (!element) {
            alert("Error: No PDF content to generate.");
            setDownloadPdfFlag(false);
            setPdfData(null);
            return;
        }

        const patientId = pdfData.patientId || "N/A";
        const reportNumber = pdfData.reportNumber || 1;
        const fileName = `Report_${patientId}_${reportNumber}.pdf`;

        try {
            html2pdf()
                .from(element)
                .set({
                    filename: fileName,
                    image: { type: "jpeg", quality: 1.0 },
                    html2canvas: { scale: 3, useCORS: true },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                    timeout: 10000
                })
                .toCanvas()
                .toPdf()
                .then(function () {
                    removeBlankPdfPages(this.prop.canvas, this.prop.pdf, this.prop.pageSize.inner.ratio);
                })
                .save()
                .then(() => {
                    setDownloadPdfFlag(false);
                    setPdfData(null);
                })
                .catch((error) => {
                    setDownloadPdfFlag(false);
                    setPdfData(null);
                });
        } catch (error) {
            setDownloadPdfFlag(false);
            setPdfData(null);
        }
    };

    const handleExportReport = async () => {
        try {
            // Get images
            const leftImage = formik.values.image_file?.left?.image;
            const rightImage = formik.values.image_file?.right?.image;
            
            // Convert images to data URLs - same as ExportDialog
            const blobURLs = await imagetoBase64(leftImage, rightImage);
            
            // Get classification data
            const leftClassification = formik.values.image_file?.left?.classification;
            const rightClassification = formik.values.image_file?.right?.classification;
            
            // Get gradability
            const leftGradability = formik.values.image_file?.left?.gradability;
            const rightGradability = formik.values.image_file?.right?.gradability;
            
            // Get inference text - use handleSetDR with model_outputs for both old and new formats
            const leftInference = handleSetDR(
                leftClassification, 
                leftGradability,
                formik.values.image_file?.left?.model_outputs
            );
            const rightInference = handleSetDR(
                rightClassification, 
                rightGradability,
                formik.values.image_file?.right?.model_outputs
            );
            
            // Get DME information from formik
            const leftDME = formik.values.image_file?.left?.model_outputs?.drdme?.DME_grade || null;
            const rightDME = formik.values.image_file?.right?.model_outputs?.drdme?.DME_grade || null;
            
            // Get operator override information from formik
            const operatorOverride = {
                left: {
                    fundus: formik.values.image_file?.left?.operator_override_fundus || false,
                    eye: formik.values.image_file?.left?.operator_override_eye || false
                },
                right: {
                    fundus: formik.values.image_file?.right?.operator_override_fundus || false,
                    eye: formik.values.image_file?.right?.operator_override_eye || false
                }
            };
            
            // Get next steps
            const { date, message } = nextSteps(leftInference, rightInference, leftDME, rightDME, operatorOverride);
            
            // Prepare PDF data - same structure as ExportDialog
            const patientId = formik.values.pat_info.otherId || "N/A";
            const reportNumber = visitNumber != null ? visitNumber : (isOnline ? 1 : null);
            
            const reportDataObj = {
                patientId: patientId,
                reportNumber: reportNumber,
                heading: "Diabetic Retinopathy Screening Report",
                reportindex: reportNumber,
                visitDate: formatDate(new Date()),
                patientDetails: [
                    { label: "Patient ID", value: patientId },
                    { label: "Age", value: `${formik.values.pat_info.age} Y` },
                    { label: "Gender", value: formik.values.pat_info.gender },
                    { 
                        label: "Diabetes",
                        value: (() => {
                            const diabetes = formik.values.pat_info.diabetes || '';
                            if (diabetes === "Yes" && (formik.values.pat_info.diabetesDurationYears || formik.values.pat_info.diabetesDurationMonths)) {
                                const years = formik.values.pat_info.diabetesDurationYears || 0;
                                const months = formik.values.pat_info.diabetesDurationMonths || 0;
                                const parts = [];
                                if (years) {
                                    parts.push(`${years}year${years > 1 ? 's' : ''}`);
                                }
                                if (months) {
                                    parts.push(`${months}month${months > 1 ? 's' : ''}`);
                                }
                                if (parts.length > 0) {
                                    return `${diabetes}(${parts.join(', ')})`;
                                }
                            }
                            return diabetes;
                        })()
                    },
                    { 
                        label: "Patient Reported Vision Issues", 
                        value: (() => {
                            const distantLeft = formik.values.pat_info.visualAcuity?.distant?.left?.trim() || '';
                            const distantRight = formik.values.pat_info.visualAcuity?.distant?.right?.trim() || '';
                            const nearLeft = formik.values.pat_info.visualAcuity?.near?.left?.trim() || '';
                            const nearRight = formik.values.pat_info.visualAcuity?.near?.right?.trim() || '';
                            
                            if (distantLeft || distantRight || nearLeft || nearRight) {
                                const parts = [];
                                if (distantLeft || distantRight) {
                                    const distantParts = [];
                                    if (distantLeft) distantParts.push(`${distantLeft} Left`);
                                    if (distantRight) distantParts.push(`${distantRight} Right`);
                                    parts.push(`Distant Vision - ${distantParts.join(', ')}`);
                                }
                                if (nearLeft || nearRight) {
                                    const nearParts = [];
                                    if (nearLeft) nearParts.push(`${nearLeft} Left`);
                                    if (nearRight) nearParts.push(`${nearRight} Right`);
                                    parts.push(`Near Vision - ${nearParts.join(', ')}`);
                                }
                                return parts.join(', ') || "Unknown";
                            }
                            
                            // Fallback to old structure
                            const left = formik.values.pat_info.visualAcuity?.left?.trim() || '';
                            const right = formik.values.pat_info.visualAcuity?.right?.trim() || '';
                            if (left && right) {
                                return `Left: ${left}, Right: ${right}`;
                            } else if (left) {
                                return `Left: ${left}`;
                            } else if (right) {
                                return `Right: ${right}`;
                            }
                            return "Unknown";
                        })()
                    },
                ],
                files: {
                    left: blobURLs.left,
                    right: blobURLs.right
                },
                date: date,
                message: message,
                leftInference: leftInference,
                rightInference: rightInference,
                leftDME: leftDME,
                rightDME: rightDME,
                operatorOverride: operatorOverride,
                required: inference.req,
                singleImageReason: formik.values.image_file?.single_image_reason,
                disclaimer: "MadhuNetrAI, an AI-based Diabetic Retinopathy Screening application, has been developed under the guidance and supervision of qualified doctors from AIIMS New Delhi and AIIMS Rishikesh. However, the AI interpretation is only a prediction, and the final decision rests with the attending doctor. The E-Health division of the Ministry of Health and Family Welfare & Wadhwani AI—the AI Unit of the Lords Education and Health Society (LEHS)—disclaims any liability, claims, or disputes arising from the use of this application. The information, including but not limited to text, graphics, images, and other material contained in this application, are for informational purposes only. No material on this application is intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider and never disregard professional medical advice or delay in seeking it because of something you have read or seen on this application."
            };
            
            setPdfData(reportDataObj);
            setDownloadPdfFlag(true);
        } catch (error) {
            alert("Error exporting report. Please try again.");
            setDownloadPdfFlag(false);
            setPdfData(null);
        }
    };

    // Generate PDF when pdfData is ready - same pattern as ExportDialog
    useEffect(() => {
        if (downloadPdfFlag && pdfData) {
            generatePDF();
        }
    }, [pdfData, downloadPdfFlag]);
  return (
    <div>
        <div>
        <Stack direction="row" padding="10px 5%" justifyContent="space-between" alignItems="center" >
            <Typography fontWeight={600} fontSize="20px" color="rgba(0, 0, 0, 0.6)">Screening Report</Typography>
            <div style={{ marginLeft: 'auto' }}>
                <Button
                    className='homeButton'
                    sx={{ borderRadius: "8px", color: "#0D1E83", border: "1px solid #0D1E83", textTransform:"none" }}
                    startIcon={<HomeIcon />}
                    onClick={handleHomeClick}
                >
                    Home
                </Button>
            </div>
        </Stack>
        </div>
        <div style={{padding: isMobile ? "0%" : "2.5% 12.5%" }}>
            <Box sx={{
                border: "1px solid var(--BoxStroke, rgba(0, 0, 0, 0.05))",
                borderRadius: "8px",
                background: "var(--White, #FFF)",
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 6px 0px rgba(0, 0, 0, 0.02)",
            }}>
                <Box width="100%" sx={{background: "rgba(189, 215, 242, 0.14)", paddingBottom: "10px"}}>
                      {loadingVisitNumber ? (
                          <ProcessingScreen />
                      ) : (
                          <PatientDetailsComponent
                              edit={false}
                              handlePatientDetailsEditClick={handlePatientDetailsEditClick}
                              pat_name={formik.values.pat_info.name}
                              pat_age={formik.values.pat_info.age}
                              pat_sex={formik.values.pat_info.gender}
                              pat_diabetes={formik.values.pat_info.diabetes}
                              diabetes_duration_years={formik.values.pat_info.diabetesDurationYears}
                              diabetes_duration_months={formik.values.pat_info.diabetesDurationMonths}
                              visual_acuity_distant_left={formik.values.pat_info.visualAcuity?.distant?.left}
                              visual_acuity_distant_right={formik.values.pat_info.visualAcuity?.distant?.right}
                              visual_acuity_near_left={formik.values.pat_info.visualAcuity?.near?.left}
                              visual_acuity_near_right={formik.values.pat_info.visualAcuity?.near?.right}
                              vision_issues={formik.values.pat_info.visualAcuity 
                                  ? `Left: ${formik.values.pat_info.visualAcuity.left || ''}, Right: ${formik.values.pat_info.visualAcuity.right || ''}`
                                  : ''}
                              visitNumber={visitNumber != null ? visitNumber : (isOnline ? 1 : null)}
                              visitDate={formatDate(new Date())}
                              pat_id={formik.values.pat_info.otherId === "" ? "N/A" : formik.values.pat_info.otherId}
                              pat_phone={formik.values.pat_info.number === "" ? "N/A" : formik.values.pat_info.number}
                          />
                      )}
                </Box>
                <Box sx={{padding: "1% 7.5%"}}>
                    <InferenceDetailsComponent
                        edit={false}
                        handleImageEditClick={handleImageEditClick}
                        leftImage={leftImageSrc}
                        rightImage={rightImageSrc}
                        leftInference={handleSetDR(
                            formik.values.image_file?.left?.classification,
                            formik.values.image_file?.left?.gradability,
                            formik.values.image_file?.left?.model_outputs
                        ) || inference.left}
                        rightInference={handleSetDR(
                            formik.values.image_file?.right?.classification,
                            formik.values.image_file?.right?.gradability,
                            formik.values.image_file?.right?.model_outputs
                        ) || inference.right}
                        required={(() => {
                            const computed = handleSetReferral(
                                getInferenceFromData(formik.values.image_file?.left?.classification, formik.values.image_file?.left?.model_outputs) || inference.left,
                                getInferenceFromData(formik.values.image_file?.right?.classification, formik.values.image_file?.right?.model_outputs) || inference.right,
                                formik.values.image_file?.single_image_reason,
                                {
                                    left: { fundus: formik.values.image_file?.left?.operator_override_fundus || false, eye: formik.values.image_file?.left?.operator_override_eye || false },
                                    right: { fundus: formik.values.image_file?.right?.operator_override_fundus || false, eye: formik.values.image_file?.right?.operator_override_eye || false }
                                },
                                {
                                    left: formik.values.image_file?.left?.model_outputs?.drdme?.DME_grade || null,
                                    right: formik.values.image_file?.right?.model_outputs?.drdme?.DME_grade || null
                                }
                            );
                            if (computed === "Required" || computed === "Not Required") return computed;
                            if (inference.req === "Required" || inference.req === "Not Required") return inference.req;
                            return computed ?? null;
                        })()}
                        formik={formik}
                        modelOutputs={{
                            left: formik.values.image_file?.left?.model_outputs || null,
                            right: formik.values.image_file?.right?.model_outputs || null
                        }}
                        operatorOverride={{
                            left: {
                                fundus: formik.values.image_file?.left?.operator_override_fundus || false,
                                eye: formik.values.image_file?.left?.operator_override_eye || false
                            },
                            right: {
                                fundus: formik.values.image_file?.right?.operator_override_fundus || false,
                                eye: formik.values.image_file?.right?.operator_override_eye || false
                            }
                        }}
                    />
                </Box>
                
                <Accordion defaultExpanded={true}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{padding: "0 7.5%", display: "flex", alignContent: "center", background: "#BDD7F21F"}}>
                        <Typography fontWeight={600} fontSize="20px" color="#181818">Disclaimer</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{padding: "2% 7.5%"}}>
                        <DisclaimerComponent />
                    </AccordionDetails>
                </Accordion>
                <Box sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: isMobile ? "center" : "flex-end",
                    flexWrap: "wrap",
                    gap: isMobile ? "12px" : "16px",
                    padding: isMobile ? "16px" : "1.5% 7.5%",
                    borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                    boxSizing: "border-box"
                }}>
                    <Button
                        onClick={handleExportReport}
                        startIcon={<UploadIcon />}
                        sx={{
                            borderRadius: "8px",
                            border: "1px solid #0D1E83",
                            color: "#0D1E83",
                            background: "#FFFFFF",
                            textTransform: "none",
                            padding: isMobile ? "10px 16px" : "12px 24px",
                            fontSize: isMobile ? "14px" : "16px",
                            minWidth: isMobile ? "auto" : "auto",
                            flex: isMobile ? "1 1 auto" : "0 0 auto",
                            maxWidth: isMobile ? "48%" : "none",
                            "&:hover": {
                                backgroundColor: "rgba(13, 30, 131, 0.04)"
                            }
                        }}
                    >
                        Export Report
                    </Button>
                        <Button
                        onClick={handleAddNewPatient}
                        startIcon={<AddIcon />}
                            sx={{
                                borderRadius: "8px",
                            border: "1px solid #0D1E83",
                            background: "#0D1E83",
                                color: "#FFFFFF",
                                textTransform: "none",
                            padding: isMobile ? "10px 16px" : "12px 24px",
                            fontSize: isMobile ? "14px" : "16px",
                            minWidth: isMobile ? "auto" : "auto",
                            flex: isMobile ? "1 1 auto" : "0 0 auto",
                            maxWidth: isMobile ? "48%" : "none",
                            "&:hover": {
                                backgroundColor: "#0A1A6B"
                            }
                        }}
                    >
                        Add New Patient
                        </Button>
                </Box>
                <LoadingComponent open={submittingReport} text={true} />
            </Box>
        </div>
        <div style={{ display: "none" }}>
            <div id="pdf-content">
                {downloadPdfFlag && pdfData && (
                    <PdfTemplate
                        downloadFlag={false}
                        fileName={`Report_${pdfData.patientId}_${pdfData.reportNumber}`}
                        heading={pdfData.heading}
                        reportindex={pdfData.reportindex}
                        visitDate={pdfData.visitDate}
                        patientDetails={pdfData.patientDetails}
                        images={{ 
                            img1: pdfData.files?.left || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4K", 
                            img2: pdfData.files?.right || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4K"
                        }}
                        leftInference={pdfData.leftInference}
                        rightInference={pdfData.rightInference}
                        leftDME={pdfData.leftDME}
                        rightDME={pdfData.rightDME}
                        operatorOverride={pdfData.operatorOverride || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } }}
                        required={pdfData.required}
                        singleImageReason={pdfData.singleImageReason}
                        nextSteps={[null, pdfData.message]}
                        disclaimer={pdfData.disclaimer}
                    />
                )}
            </div>
        </div>
    </div>
  )
}

export default PatientInference